import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  alpha,
  useTheme,
} from '@mui/material'
import { InfoOutlined } from '@mui/icons-material'
import { useAuthContext } from '../../context/auth'
import AvatarEditor from './AvatarEditor'

// My profile card: same look and avatar editor as the "Edit user details" dialog
const ProfileAvatarDialog = ({ open, onClose, profile }) => {
  const theme = useTheme()
  const { auth } = useAuthContext()

  const isDark = theme.palette.mode === 'dark'
  const accent = theme.palette.secondary.main

  const roles = [].concat(auth?.roles ?? [])
  const user = profile ? { ...profile, _id: profile._id || auth?._id } : null

  const dialogPaperSx = {
    backgroundColor: theme.palette.background.alt,
    color: theme.palette.text.primary,
    borderRadius: 2,
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : theme.palette.neutral[200]}`,
    boxShadow: 8,
    overflow: 'hidden',
    width: '100%',
    maxWidth: 320,                    // the card is as narrow as the left column of "Edit user details"
  }

  const cancelButtonSx = {
    color: theme.palette.text.primary,
    fontWeight: 600,
    '&:hover': { backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' },
  }

  // Tells useMyProfile (Navbar avatar, menu) about the new picture right away
  const publish = (avatar) =>
    window.dispatchEvent(new CustomEvent('profile:changed', { detail: { avatar } }))

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" PaperProps={{ sx: dialogPaperSx }}>
      <DialogTitle
        sx={{
          px: 3,
          py: 2,
          fontWeight: 600,
          color: theme.palette.text.primary,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        My profile
      </DialogTitle>

      <DialogContent
        sx={{
          px: 3,
          pb: 3,
          // '&&' outranks MUI's "DialogTitle + DialogContent { padding-top: 0 }" rule
          '&&': { pt: 3 },
          backgroundColor: theme.palette.background.alt,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            p: 2.5,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : alpha(accent, 0.05),
          }}
        >
          {user && <AvatarEditor user={user} onChanged={publish} menu />}

          <Box textAlign="center" width="100%" minWidth={0}>
            <Typography variant="h6" noWrap sx={{ fontWeight: 700 }}>
              {profile?.name || auth?.name}
            </Typography>
            {profile?.email && (
              <Typography variant="body2" noWrap color="text.secondary">
                {profile.email}
              </Typography>
            )}
          </Box>

          {roles.length > 0 && (
            <Box display="flex" flexWrap="wrap" gap={0.75} justifyContent="center">
              {roles.map((role) => (
                <Chip
                  key={role}
                  size="small"
                  variant="outlined"
                  label={role}
                  sx={{ color: accent, borderColor: alpha(accent, 0.6), fontWeight: 600 }}
                />
              ))}
            </Box>
          )}
        </Box>

        <Box display="flex" alignItems="center" gap={0.75} mt={2}>
          <InfoOutlined sx={{ fontSize: '1.1rem', color: theme.palette.text.secondary }} />
          <Typography variant="caption" color="text.secondary">
            JPG, PNG, WEBP or GIF, up to 5 MB. You can move, zoom and rotate it before saving.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 1.75,
          borderTop: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.alt,
        }}
      >
        <Button onClick={onClose} sx={cancelButtonSx}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ProfileAvatarDialog
